// src/components/Vendors/VendorsManager.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  fetchVendors,
  insertVendor,
  updateVendor,
  patchVendorStatus,
  deleteVendor,
} from "../../../store/vendorsSlice";

import "../../../Styles/Common.css";

const VendorsManager = () => {
  const dispatch = useDispatch();
  const { vendors = [], loading, error } = useSelector((s) => s.vendors || {});
  const sanitizeUrl = (url) => url.replace(/([^:])\/\//g, "$1/");

  const [showPopup, setShowPopup] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [form, setForm] = useState({
    id: "",
    vendor: "",
    vendorDesc: "",
    icon: "",        // saved string path from backend: "/static/images/vendors/xxx.jpg"
    iconFile: null,  // File selected by user
    status: "A",
  });

  useEffect(() => {
    dispatch(fetchVendors());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === "string" ? error : JSON.stringify(error), {
        toastId: "vendors-err",
      });
    }
  }, [error]);

  const resetForm = () => {
    setForm({
      id: "",
      vendor: "",
      vendorDesc: "",
      icon: "",
      iconFile: null,
      status: "A",
    });
  };

  const handleOpenInsert = () => {
    resetForm();
    setEditMode(false);
    setShowPopup(true);
  };

  const handleOpenEdit = (v) => {
    setForm({
      id: v.id,
      vendor: v.vendor || "",
      vendorDesc: v.vendorDesc || "",
      icon: v.icon || "",
      iconFile: null,
      status: v.status || "A",
    });
    setEditMode(true);
    setShowPopup(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setForm((p) => ({ ...p, iconFile: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.vendor.trim()) {
      toast.error("Vendor name is required.");
      return;
    }

    try {
      if (editMode) {
        await dispatch(
          updateVendor({
            id: form.id,
            vendor: form.vendor,
            vendorDesc: form.vendorDesc,
            status: form.status,
            iconFile: form.iconFile, // ✅ file goes with key "icon" in thunk
          })
        ).unwrap();
        toast.success("Vendor updated successfully!");
      } else {
        await dispatch(
          insertVendor({
            vendor: form.vendor,
            vendorDesc: form.vendorDesc,
            status: "A",
            iconFile: form.iconFile, // ✅ required for insert (backend allows None but you want upload)
          })
        ).unwrap();
        toast.success("Vendor inserted successfully!");
      }

      setShowPopup(false);
      resetForm();
    } catch (err) {
      toast.error("Failed to save vendor.");
      console.error(err);
    }
  };

  const handleToggleStatus = async (v) => {
    const newStatus = v.status === "A" ? "D" : "A";
    try {
      await dispatch(patchVendorStatus({ id: v.id, status: newStatus })).unwrap();
      toast.success(`Vendor ${newStatus === "A" ? "Activated" : "Deactivated"}`);
    } catch (err) {
      toast.error("Failed to toggle vendor status.");
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;
    try {
      await dispatch(deleteVendor(id)).unwrap();
      toast.success("Vendor deleted successfully!");
    } catch (err) {
      toast.error("Failed to delete vendor.");
      console.error(err);
    }
  };

  const toImgUrl = (path) => {
    if (!path) return "";
    // backend stores /static/images/vendors/xxx.jpg
    return sanitizeUrl(`https://iteyonikservicesinstitute.com/${String(path).replace(/^\//, "")}`);
  };

  return (
    <div className="trainer-container">
      <ToastContainer position="bottom-right" autoClose={3000} />

      <div className="header-container">
        <h2>Vendor Management</h2>
        <button className="add-lead-btn" onClick={handleOpenInsert}>
          <i className="fa fa-plus" />
        </button>
      </div>

      {loading && <p className="loading">Loading vendors...</p>}

      <table className="table trainer-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Vendor</th>
            <th>Description</th>
            <th>Icon</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {vendors.map((v, idx) => (
            <tr key={v.id}>
              <td>{idx + 1}</td>
              <td>{v.vendor}</td>
              <td style={{ maxWidth: 450 }}>{v.vendorDesc}</td>

              <td>
                {v.icon ? (
                  <img
                    src={toImgUrl(v.icon)}
                    alt={v.vendor || "Vendor"}
                    className="ict-img"
                    width="80"
                    height="50"
                    style={{ objectFit: "cover", borderRadius: 6 }}
                  />
                ) : (
                  "—"
                )}
              </td>

              <td>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={v.status === "A"}
                    onChange={() => handleToggleStatus(v)}
                  />
                  <span className="slider round" />
                </label>
              </td>

              <td>{v.created ? new Date(v.created).toLocaleString() : "-"}</td>

              <td>
                <button
                  className="icon-btn update-btn fa fa-edit"
                  onClick={() => handleOpenEdit(v)}
                />
                <button
                  className="icon-btn delete-btn fa fa-trash"
                  onClick={() => handleDelete(v.id)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{editMode ? "Update Vendor" : "Insert Vendor"}</h3>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Vendor Name</label>
                <input
                  type="text"
                  name="vendor"
                  value={form.vendor}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Vendor Description</label>
                <textarea
                  name="vendorDesc"
                  rows="3"
                  value={form.vendorDesc}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Upload Icon (png/jpg/jpeg)</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleFileChange}
                  required={!editMode} // required for insert, optional for update
                />
                {editMode && form.icon && (
                  <p style={{ fontSize: "0.85em", marginTop: 6 }}>
                    Current: <b>{String(form.icon).split("/").pop()}</b>
                  </p>
                )}
              </div>

              {/* preview existing icon */}
              {editMode && form.icon ? (
                <div style={{ marginTop: 8 }}>
                  <small>Preview:</small>
                  <br />
                  <img
                    src={toImgUrl(form.icon)}
                    alt="Vendor Icon"
                    width="120"
                    height="70"
                    style={{ objectFit: "cover", borderRadius: 6, marginTop: 6 }}
                  />
                </div>
              ) : null}

              <div className="form-actions">
                <button type="submit">
                  <i className={editMode ? "fa fa-edit" : "fa fa-plus-square"} />
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowPopup(false);
                    resetForm();
                  }}
                >
                  <i className="fa fa-close" />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorsManager;
